import React, { useRef, useState, useMemo, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { AiFillEyeInvisible } from "react-icons/ai";
import { BsFillEyeFill } from "react-icons/bs";
import { colorMap } from "../Utilitie";

const StackedColumnChart = ({
  data,
  from,
  chartType,
  setLegendsData,
  height,
  needTotal,
}) => {
  // console.log(data);
  const [hideShowLegend, setHideShowLegend] = useState(true);
  const hoveredSeriesRef = useRef(null);
  const [selectedLegends, setSelectedLegends] = useState({});
  const [filterNumber, setFilterNumber] = useState(null);
  const [filterAction, setFilterAction] = useState("clear");
  const [filteredData, setFilteredData] = useState(data);

  const handleSelect = (e) => setFilterAction(e.target.value);
  const handleInput = (e) => setFilterNumber(e.target.value);

  useEffect(() => {
    if (filterAction === "clear") {
      setFilterNumber("");
    }
  }, [filterAction]);

  useEffect(() => {
    if (!data?.series || !data?.categories) return;
    const number = parseInt(filterNumber, 10);
    if (filterAction === "clear" || isNaN(number)) {
      setFilteredData(data);
      return;
    }
    const newCategories =
      filterAction === "start"
        ? data.categories.slice(0, number)
        : data.categories.slice(-number);

    const newSeries = data.series.map((s) => ({
      ...s,
      data:
        filterAction === "start"
          ? s.data.slice(0, number)
          : s.data.slice(-number),
    }));

    setFilteredData({ ...data, series: newSeries, categories: newCategories });
  }, [filterAction, filterNumber, data]);

  const barSeries = filteredData?.series?.filter(
    (item) => item.name !== "total"
  );

  useEffect(() => {
    if (barSeries?.length) {
      const initialState = {};
      barSeries.forEach((s) => (initialState[s.name] = true));
      setSelectedLegends(initialState);
    }
  }, [filteredData]);

  useEffect(() => {
    const newState = {};
    barSeries?.forEach((s) => (newState[s.name] = hideShowLegend));
    setSelectedLegends(newState);
  }, [hideShowLegend]);

  const visibleSeries = useMemo(() => {
    if (!selectedLegends || Object.keys(selectedLegends).length === 0)
      return barSeries;
    return barSeries.filter((s) => selectedLegends[s.name] !== false);
  }, [selectedLegends, barSeries]);

  const sortedfilteredData = useMemo(() => {
    if (!filteredData?.categories?.length)
      return { categories: [], series: [], totals: [] };

    const categories = filteredData.categories;
    const enrichedSeries = barSeries.map((s) => ({
      ...s,
      filteredData: s.data,
    }));

    if (from === "commodity") {
      const totals = categories.map((_, idx) =>
        visibleSeries.reduce((sum, s) => sum + (Number(s.data[idx]) || 0), 0)
      );
      return { categories, series: enrichedSeries, totals };
    }

    const totals = categories.map((_, idx) =>
      visibleSeries.reduce((sum, s) => sum + (Number(s.data[idx]) || 0), 0)
    );

    const sortedIndices = totals
      .map((total, index) => ({ index, total }))
      .sort((a, b) => b.total - a.total)
      .map((item) => item.index);

    const sortedCategories = sortedIndices.map((i) => categories[i]);
    const sortedSeries = enrichedSeries.map((s) => ({
      ...s,
      filteredData: sortedIndices.map((i) => s.data[i]),
    }));
    const sortedTotals = sortedIndices.map((i) => totals[i]);

    return {
      categories: sortedCategories,
      series: sortedSeries,
      totals: sortedTotals,
    };
  }, [visibleSeries, filteredData, from]);

  const barSeriesWithGrayEffect = sortedfilteredData.series.map((item) => {
    const isSelected = selectedLegends[item.name] !== false;
    return {
      name: item.name,
      type: chartType || "bar",
      stack: "total",
      data: isSelected ? item.filteredData : item.filteredData.map(() => 0),
      itemStyle: {
        color: isSelected ? colorMap[item.name] || "#ccc" : "#e0e0e0",

        // color: color,
        shadowColor: "rgba(0, 0, 0, 0.4)",
        shadowBlur: 5,
        shadowOffsetX: 4,
        shadowOffsetY: 0,
        // borderRadius: [4, 4, 0, 0], // optional, for rounded bars
      },
      label: {
        show: true,
        fontSize: 10,
        color: isSelected ? "#000000" : "#999",
        formatter: (params) => {
          if (
            params.value === 0 ||
            from === "trainDeTCode" ||
            from === "commodity"
          )
            return "";
          if (from === "CHWeight") {
            return `${(params.value / 1000000).toFixed(2)}M`;
          }
          if (from === "Wagon") {
            return `${(params.value / 1000).toFixed(2)}K`;
          }
          return params.value;
        },
      },
      emphasis: {
        disabled: !isSelected,
      },
    };
  });

  const series = [
    ...barSeriesWithGrayEffect,
    {
      name: "Total",
      type: "custom",
      renderItem: (params, api) => {
        const categoryIndex = api.value(0);
        const total = api.value(1);
        const point = api.coord([categoryIndex, total]);
        return {
          type: "text",
          style: {
            text:
              from === "commodity"
                ? (total / 1000000).toFixed(2).toString()
                : total.toString(),
            x: point[0],
            y: point[1] - 10,
            textAlign: "center",
            textVerticalAlign: "bottom",
            fontSize: needTotal === "no" ? 0 : 10,
            fontWeight: "bold",
            fill: "#000",
            label: {
              show: false,
              fontSize: 10,
              color: "#000",
              formatter: (params) => {
                if (
                  params.value === 0 ||
                  from === "trainDeTCode" ||
                  from === "commodity"
                )
                  return "";
                if (from === "CHWeight") {
                  return `${(params.value / 1000000).toFixed(2)}M`;
                }
                if (from === "Wagon") {
                  return `${(params.value / 1000).toFixed(2)}K`;
                }
                return params.value;
              },
            },
          },
        };
      },
      data: sortedfilteredData.totals.map((t, i) => [i, t]),
      z: 2, // Maintained low z-index for Total series
    },
  ];

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      textStyle: { fontSize: 12 },
      formatter: (params) => {
        if (!params || !params.length) return "";
        let tooltipText = `${params[0].name}<br/>`;
        params.forEach((item) => {
          if (
            !hoveredSeriesRef.current ||
            hoveredSeriesRef.current === item.seriesName
          ) {
            if (item.value === 0 || item.seriesName === "Total") return;
            tooltipText += `${item.marker} ${item.seriesName}: ${item.value}<br/>`;
          }
        });
        return tooltipText;
      },
    },
    lineStyle: {
      // color: color,
      shadowColor: "rgba(0, 0, 0, 0.4)",
      shadowBlur: 5,
      shadowOffsetX: 4,
      shadowOffsetY: 0,
      // borderRadius: [4, 4, 0, 0], // optional, for rounded bars
    },
    itemStyle: {
      // color: color,
      shadowColor: "rgba(0, 0, 0, 0.4)",
      shadowBlur: 5,
      shadowOffsetX: 4,
      shadowOffsetY: 0,
      // borderRadius: [4, 4, 0, 0], // optional, for rounded bars
    },
    legend: {
      data: barSeries.map((i) => i.name.toUpperCase()),
      top: "0%",
      right: "200px",
      textStyle: { fontSize: 11 },
    },
    grid: {
      top: barSeries.length < 15 ? "15%" : "20%",
      left: "1%",
      right: "5%",
      bottom: "10%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: sortedfilteredData.categories,
      axisTick: { alignWithLabel: true },
      axisLabel: {
        fontSize: 11,
        interval: 0,
        rotate: sortedfilteredData.categories.length > 1 ? 40 : 0,
      },
    },
    yAxis: {
      type: "value",
      axisLabel: { fontSize: 11 },
    },
    dataZoom:
      sortedfilteredData.categories.length > 1
        ? [
            {
              type: "slider",
              show: true,
              xAxisIndex: 0,
              start: 0,
              end:
                sortedfilteredData.categories.length > 200
                  ? 20
                  : sortedfilteredData.categories.length > 150
                  ? 30
                  : sortedfilteredData.categories.length > 100
                  ? 50
                  : sortedfilteredData.categories.length > 30
                  ? 80
                  : 100,
              height: 25,
              bottom: 0,
              realtime: true,
              throttle: 40,
            },
            {
              type: "inside",
              xAxisIndex: 0,
              start: 0,
              end: 20,
              realtime: true,
              throttle: 500,
            },
          ]
        : [],
    series,
    animationDuration: 1500,
    animationEasing: "cubicOut",
    animationDurationUpdate: 1500,
    animationEasingUpdate: "cubicOut",
  };

  //////////////////////////////////////////////////////////////////////ON EVENTS///////////////////////////////////
  useEffect(() => {
    if (from === "trainDeTCode") {
      setLegendsData((prev) => ({
        barClicked: null,
        barLegendName: null,
        legendChanged: selectedLegends,
      }));
    }
  }, [selectedLegends, from]);

  const onChartClick = (params) => {
    if (from === "trainDeTCode") {
      setLegendsData((prev) => ({
        legendChanged: null,
        barClicked: params.name,
        barLegendName: params.seriesName,
      }));
    }
  };
  const onEvents = {
    mouseover: (params) => {
      if (params?.seriesName) hoveredSeriesRef.current = params.seriesName;
    },
    mouseout: () => {
      hoveredSeriesRef.current = null;
    },
    legendselectchanged: (params) => {
      setSelectedLegends((prev) => ({
        ...prev,
        [params.name]: !prev[params.name],
      }));
    },
    click: onChartClick,
  };

  return (
    <div style={{ width: "100%", height: height || "300px", zIndex: 1 }}>
      <div className="position-relative">
        <div className="position-absolute top-0 end-0" style={{ zIndex: 2 }}>
          <div className="row justify-content-end" style={{ fontSize: "12px" }}>
            <div className="col-md-4 d-flex justify-content-end">
              <OverlayTrigger
                overlay={
                  <Tooltip id="tooltip-1">
                    {hideShowLegend
                      ? "hide all categories"
                      : "show all categories"}
                  </Tooltip>
                }
              >
                <button
                  className="btn btn-outline-dark btn-sm"
                  onClick={() => setHideShowLegend(!hideShowLegend)}
                >
                  {hideShowLegend ? <AiFillEyeInvisible /> : <BsFillEyeFill />}
                </button>
              </OverlayTrigger>
            </div>
            <div className="col-md-4 d-flex justify-content-end">
              <OverlayTrigger
                overlay={
                  <Tooltip id="tooltip-1">
                    insert a No. from 0 to {data.categories.length} to filter
                    data
                  </Tooltip>
                }
              >
                <input
                  onChange={handleInput}
                  type="number"
                  value={filterNumber}
                  min={0}
                  max={data.categories.length}
                  placeholder={`10`}
                  style={{ maxWidth: "50px" }}
                />
              </OverlayTrigger>
            </div>
            <div
              className="col-md-4 d-flex justify-content-end"
              style={{ maxWidth: "50px" }}
            >
              <select onChange={handleSelect}>
                <option value="clear">Clear</option>
                <option value="start">Start</option>
                <option value="end">End</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <ReactECharts
        option={option}
        notMerge={true}
        style={{ height: "100%", zIndex: 1 }}
        onEvents={onEvents}
      />
    </div>
  );
};

export default StackedColumnChart;
